import type { ChartEvent, Plugin, TooltipModel } from "chart.js";

import { getViewportTransform, mapPointToViewport } from "../selection-zoom/geometry";
import {
  drawViewportAngularScaleLabels,
  getOrbitRadialScale,
  POLAR_ANGLE_INDEX_OFFSET,
  type IViewportAngularScaleLabelPluginOptions,
  type OrbitRadialScale,
} from "./angular-scale-labels";

export const ARROW_LINE_WIDTH = 2;
export const ARROW_HEAD_LENGTH = 12;
export const ARROW_HEAD_ANGLE = Math.PI / 8;
export const ARROW_COLOR = "#1941A9";
export const ANGLE_INDEX_OFFSET = POLAR_ANGLE_INDEX_OFFSET;
const ORBIT_MARKER_RADIUS = 2.25;
const ORBIT_ZERO_MARKER_RADIUS = 3.2;
export const ORBIT_MARKER_POINTS_LIMIT = 20_000;
export const ORBIT_MARKER_POINTS_SAMPLE_STEP = 7;
export const ORBIT_PATH_POINTS_LIMIT = 16_000;

type OrbitPoint = {
  index: number;
  x: number;
  y: number;
  theta: number;
  r: number;
  isZero: boolean;
};

type OrbitPointWithCoords = OrbitPoint & {
  canvasX: number;
  canvasY: number;
};

export const selectOrbitMarkerPoints = <T extends Pick<OrbitPoint, "isZero">>(points: T[]): T[] => {
  const markerPoints = points.filter(point => point.isZero);

  if (markerPoints.length <= ORBIT_MARKER_POINTS_LIMIT) {
    return markerPoints;
  }

  return markerPoints.filter((_point, index) => index % ORBIT_MARKER_POINTS_SAMPLE_STEP === 0);
};

export const selectOrbitPathPoints = <T>(points: T[]): T[] => {
  if (points.length <= ORBIT_PATH_POINTS_LIMIT) {
    return points;
  }

  const step = Math.ceil(points.length / ORBIT_PATH_POINTS_LIMIT);
  const sampled: T[] = [];

  for (let index = 0; index < points.length; index += step) {
    sampled.push(points[index]);
  }

  const lastPoint = points[points.length - 1];
  if (sampled[sampled.length - 1] !== lastPoint) {
    sampled.push(lastPoint);
  }

  return sampled;
};

type OrbitTooltipDataset = {
  _orbitPathPoints?: OrbitPoint[];
  _activeOrbitTooltipItem?: OrbitPoint | null;
  orbitStrokeColor?: string;
  pointBorderColor?: string;
};

type OrbitTooltipChart = {
  _externalTooltipPoint?: { x: number; y: number } | null;
};

export interface IOrbitConnectingLinesPluginOptions extends IViewportAngularScaleLabelPluginOptions {
  viewportScale?: number;
  onPointClick?: (datasetIndex: number) => void;
}

const getPolarPointCoords = (item: OrbitPoint, rScale: OrbitRadialScale) => {
  const angleRad = rScale.getIndexAngle(item.theta + ANGLE_INDEX_OFFSET);
  const radius = rScale.getDistanceFromCenterForValue(item.r);

  return {
    canvasX: rScale.xCenter + Math.cos(angleRad) * radius,
    canvasY: rScale.yCenter - Math.sin(angleRad) * radius,
  };
};

const getViewportScale = (options?: IOrbitConnectingLinesPluginOptions | false): number => {
  if (!options) {
    return 1;
  }

  const scale = Number(options.viewportScale);

  return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

const scaleCanvasMetric = (value: number, viewportScale: number): number => value / viewportScale;

const getOrbitPointDataIndex = (point: OrbitPoint): number => {
  const theta = ((Math.round(point.theta) % 360) + 360) % 360;

  return 359 - theta;
};

const drawOrbitPointMarkers = (
  ctx: CanvasRenderingContext2D,
  pointsArrCoords: OrbitPointWithCoords[],
  strokeColor: string,
  viewportScale: number,
): void => {
  if (pointsArrCoords.length === 0) {
    return;
  }

  const pointRadius = scaleCanvasMetric(ORBIT_MARKER_RADIUS, viewportScale);
  const zeroPointRadius = scaleCanvasMetric(ORBIT_ZERO_MARKER_RADIUS, viewportScale);

  ctx.save();
  pointsArrCoords.forEach(point => {
    ctx.beginPath();
    ctx.arc(point.canvasX, point.canvasY, point.isZero ? zeroPointRadius : pointRadius, 0, 2 * Math.PI);

    if (point.isZero) {
      ctx.fillStyle = "#fff";
      ctx.fill();
      ctx.strokeStyle = "#4caf50";
      ctx.lineWidth = scaleCanvasMetric(2, viewportScale);
      ctx.stroke();
      return;
    }

    ctx.fillStyle = strokeColor;
    ctx.fill();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
    ctx.lineWidth = scaleCanvasMetric(1, viewportScale);
    ctx.stroke();
  });
  ctx.restore();
};

type TooltipActiveElement = {
  datasetIndex: number;
  index: number;
  element?: unknown;
};

type TooltipController = TooltipModel<"radar"> & {
  setActiveElements?(active: TooltipActiveElement[], eventPosition: { x: number; y: number }): void;
  _active?: TooltipActiveElement[];
  update?(change?: boolean): void;
};

const setTooltipActiveElements = (
  chart: any,
  activeElements: TooltipActiveElement[],
  position: { x: number; y: number },
): void => {
  const tooltip = chart.tooltip as TooltipController | undefined;

  if (!tooltip) {
    return;
  }

  if (typeof tooltip.setActiveElements === "function") {
    tooltip.setActiveElements(activeElements, position);
  } else {
    tooltip._active = activeElements;
  }

  tooltip.update?.(true);
};

const clearOrbitActiveTooltipItems = (chart: any): void => {
  chart.data.datasets.forEach((dataset: OrbitTooltipDataset) => {
    if (dataset._activeOrbitTooltipItem) {
      dataset._activeOrbitTooltipItem = null;
    }
  });
  (chart as OrbitTooltipChart)._externalTooltipPoint = null;
};

const getDisplayTooltipPoint = (
  chart: any,
  point: OrbitPointWithCoords,
  options?: IOrbitConnectingLinesPluginOptions | false,
): { x: number; y: number } => {
  const transform = getViewportTransform(
    {
      left: 0,
      right: chart.width,
      top: 0,
      bottom: chart.height,
    },
    options ? options.viewport : undefined,
  );

  const canvasPoint = { x: point.canvasX, y: point.canvasY };

  return transform ? mapPointToViewport(canvasPoint, transform) : canvasPoint;
};

const pointToSegmentDistanceSq = (
  point: { x: number; y: number },
  start: { canvasX: number; canvasY: number },
  end: { canvasX: number; canvasY: number },
): number => {
  const dx = end.canvasX - start.canvasX;
  const dy = end.canvasY - start.canvasY;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) {
    return (point.x - start.canvasX) ** 2 + (point.y - start.canvasY) ** 2;
  }

  const projection = Math.max(
    0,
    Math.min(1, ((point.x - start.canvasX) * dx + (point.y - start.canvasY) * dy) / lengthSq),
  );
  const closestX = start.canvasX + projection * dx;
  const closestY = start.canvasY + projection * dy;

  return (point.x - closestX) ** 2 + (point.y - closestY) ** 2;
};

export const findOrbitDatasetIndexAtCanvasPoint = (
  chart: any,
  point: { x: number; y: number },
  options?: IOrbitConnectingLinesPluginOptions | false,
): number | null => {
  const rScale = getOrbitRadialScale(chart);
  if (!rScale) {
    return null;
  }

  const transform = getViewportTransform(
    { left: 0, right: chart.width, top: 0, bottom: chart.height },
    options ? options.viewport : undefined,
  );
  const toDisplayPoint = (orbitPoint: OrbitPoint) => {
    const coords = getPolarPointCoords(orbitPoint, rScale);
    const display = transform ? mapPointToViewport({ x: coords.canvasX, y: coords.canvasY }, transform) : null;

    return {
      canvasX: display?.x ?? coords.canvasX,
      canvasY: display?.y ?? coords.canvasY,
    };
  };
  const hitRadiusSq = 10 * 10;
  let nearest: { datasetIndex: number; distanceSq: number } | null = null;

  for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex += 1) {
    const dataset = chart.data.datasets[datasetIndex] as OrbitTooltipDataset;
    if (!chart.isDatasetVisible(datasetIndex) || !Array.isArray(dataset._orbitPathPoints)) {
      continue;
    }

    const points = selectOrbitPathPoints(dataset._orbitPathPoints).map(toDisplayPoint);
    if (points.length === 1) {
      const distanceSq = (point.x - points[0].canvasX) ** 2 + (point.y - points[0].canvasY) ** 2;
      if (distanceSq <= hitRadiusSq && (!nearest || distanceSq <= nearest.distanceSq)) {
        nearest = { datasetIndex, distanceSq };
      }
      continue;
    }

    for (let index = 1; index < points.length; index += 1) {
      const distanceSq = pointToSegmentDistanceSq(point, points[index - 1], points[index]);
      if (distanceSq <= hitRadiusSq && (!nearest || distanceSq <= nearest.distanceSq)) {
        nearest = { datasetIndex, distanceSq };
      }
    }
  }

  return nearest?.datasetIndex ?? null;
};

const updateOrbitPointTooltip = (
  chart: any,
  event: ChartEvent,
  options?: IOrbitConnectingLinesPluginOptions | false,
): boolean => {
  const chartType = chart.config.type;

  if (chartType !== "radar" || !chart.tooltip) {
    return false;
  }

  const eventType = event.native?.type ?? event.type;

  if (eventType === "mouseout" || eventType === "mouseleave") {
    clearOrbitActiveTooltipItems(chart);
    setTooltipActiveElements(chart, [], { x: 0, y: 0 });
    return true;
  }

  if (eventType !== "mousemove" && eventType !== "click") {
    return false;
  }

  const selectionZoom = chart.selectionZoom as { dragging?: boolean; mouseDown?: boolean } | undefined;
  if (selectionZoom?.dragging || selectionZoom?.mouseDown) {
    return false;
  }

  if (typeof event.x !== "number" || typeof event.y !== "number") {
    return false;
  }

  const eventX = event.x;
  const eventY = event.y;
  const rScale = getOrbitRadialScale(chart);
  if (!rScale) {
    return false;
  }

  const viewportScale = getViewportScale(options);
  const hitRadius = scaleCanvasMetric(10, viewportScale);
  const hitRadiusSq = hitRadius * hitRadius;
  type OrbitTooltipHit = {
    dataset: OrbitTooltipDataset;
    datasetIndex: number;
    point: OrbitPointWithCoords;
    distanceSq: number;
  };
  let nearest: OrbitTooltipHit | null = null;
  let hasOrbitTooltipData = false;

  for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex += 1) {
    const dataset = chart.data.datasets[datasetIndex] as OrbitTooltipDataset;
    if (!chart.isDatasetVisible(datasetIndex)) {
      continue;
    }

    const pathPoints = dataset._orbitPathPoints;
    if (!Array.isArray(pathPoints) || pathPoints.length === 0) {
      continue;
    }

    hasOrbitTooltipData = true;

    for (const point of selectOrbitPathPoints(pathPoints)) {
      const coords = getPolarPointCoords(point, rScale);
      const dx = coords.canvasX - eventX;
      const dy = coords.canvasY - eventY;
      const distanceSq = dx * dx + dy * dy;

      // A later dataset is drawn on top, so it wins when the distance is equal.
      if (distanceSq > hitRadiusSq || (nearest && distanceSq > nearest.distanceSq)) {
        continue;
      }

      nearest = {
        dataset,
        datasetIndex,
        point: {
          ...point,
          ...coords,
        },
        distanceSq,
      };
    }
  }

  if (!hasOrbitTooltipData) {
    return false;
  }

  clearOrbitActiveTooltipItems(chart);

  if (!nearest) {
    setTooltipActiveElements(chart, [], { x: eventX, y: eventY });
    return true;
  }

  nearest.dataset._activeOrbitTooltipItem = nearest.point;
  (chart as OrbitTooltipChart)._externalTooltipPoint = getDisplayTooltipPoint(chart, nearest.point, options);
  setTooltipActiveElements(
    chart,
    [{ datasetIndex: nearest.datasetIndex, index: getOrbitPointDataIndex(nearest.point) }],
    { x: nearest.point.canvasX, y: nearest.point.canvasY },
  );

  if (eventType === "click" && options && options.onPointClick) {
    const selectionZoom = chart.selectionZoom as { suppressClickLock?: number } | undefined;
    if (!selectionZoom?.suppressClickLock) {
      options.onPointClick(nearest.datasetIndex);
    }
  }

  return true;
};

const drawArrow = (
  ctx: CanvasRenderingContext2D,
  head: { x: number; y: number },
  angle: number,
  color: string,
  viewportScale = 1,
) => {
  const arrowHeadLength = scaleCanvasMetric(ARROW_HEAD_LENGTH, viewportScale);

  ctx.save();
  ctx.strokeStyle = color ?? ARROW_COLOR;
  ctx.lineWidth = scaleCanvasMetric(ARROW_LINE_WIDTH, viewportScale);
  ctx.lineCap = "round";

  ctx.beginPath();
  ctx.moveTo(head.x, head.y);
  ctx.lineTo(
    head.x - arrowHeadLength * Math.cos(angle - ARROW_HEAD_ANGLE),
    head.y - arrowHeadLength * Math.sin(angle - ARROW_HEAD_ANGLE),
  );
  ctx.moveTo(head.x, head.y);
  ctx.lineTo(
    head.x - arrowHeadLength * Math.cos(angle + ARROW_HEAD_ANGLE),
    head.y - arrowHeadLength * Math.sin(angle + ARROW_HEAD_ANGLE),
  );
  ctx.stroke();

  ctx.restore();
};

const findDifferentNeighborIndex = (pointsArrCoords: OrbitPointWithCoords[], startIdx: number, step: number) => {
  const headPt = pointsArrCoords[startIdx];
  let index = startIdx + step;

  while (index >= 0 && index < pointsArrCoords.length) {
    const point = pointsArrCoords[index];

    if (point.canvasX !== headPt.canvasX || point.canvasY !== headPt.canvasY) {
      return index;
    }

    index += step;
  }

  return null;
};

const ROTATION_INDICATOR_GAP = 48;
const ROTATION_INDICATOR_ARC_RADIUS = 12;

const drawRotationIndicatorPolar = (
  chart: any,
  rotationDir: string | undefined,
  ctx: CanvasRenderingContext2D,
  viewportScale: number,
) => {
  if (!rotationDir || rotationDir === "none") return;

  const rScale = getOrbitRadialScale(chart);
  if (!rScale) return;

  const centerX = rScale.xCenter;
  const centerY = rScale.yCenter;
  const radiusPx =
    rScale.drawingArea ??
    (rScale.getDistanceFromCenterForValue ? rScale.getDistanceFromCenterForValue(rScale.max ?? 1) : 0);
  const gap = scaleCanvasMetric(ROTATION_INDICATOR_GAP, viewportScale);
  const arrowCx = centerX;
  const arrowCy = centerY;
  const arrowR = Math.max(scaleCanvasMetric(8, viewportScale), radiusPx + gap);

  ctx.save();
  ctx.strokeStyle = "#000";
  ctx.lineWidth = scaleCanvasMetric(2, viewportScale);
  ctx.lineCap = "round";

  const degToRad = (deg: number) => (deg * Math.PI) / 180;
  const rotationIndicatorStartRad = degToRad(194);
  const rotationIndicatorEndRad = degToRad(224);
  if (rotationDir === "cw") {
    ctx.beginPath();
    ctx.arc(arrowCx, arrowCy, arrowR, rotationIndicatorStartRad, rotationIndicatorEndRad, false);
    ctx.stroke();
    const headX = arrowCx + Math.cos(rotationIndicatorEndRad) * arrowR;
    const headY = arrowCy + Math.sin(rotationIndicatorEndRad) * arrowR;
    drawArrow(ctx, { x: headX, y: headY }, rotationIndicatorEndRad - Math.PI / 2 + Math.PI, "#000", viewportScale);
  } else {
    ctx.beginPath();
    ctx.arc(arrowCx, arrowCy, arrowR, rotationIndicatorEndRad, rotationIndicatorStartRad, true);
    ctx.stroke();
    const headX = arrowCx + Math.cos(rotationIndicatorStartRad) * arrowR;
    const headY = arrowCy + Math.sin(rotationIndicatorStartRad) * arrowR;
    drawArrow(ctx, { x: headX, y: headY }, rotationIndicatorStartRad + Math.PI / 2 + Math.PI, "#000", viewportScale);
  }

  ctx.restore();
};

const drawRotationIndicatorCartesian = (
  chart: any,
  rotationDir: string | undefined,
  ctx: CanvasRenderingContext2D,
  viewportScale: number,
) => {
  if (!rotationDir || rotationDir === "none") return;

  const { right, top } = chart.chartArea;
  const inset = scaleCanvasMetric(22, viewportScale);
  const arrowCx = right - inset;
  const arrowCy = top + inset;
  const arrowR = scaleCanvasMetric(ROTATION_INDICATOR_ARC_RADIUS, viewportScale);

  ctx.save();
  ctx.strokeStyle = "#000";
  ctx.lineWidth = scaleCanvasMetric(2, viewportScale);
  ctx.lineCap = "round";

  const start = (200 * Math.PI) / 180;
  const end = (340 * Math.PI) / 180;

  if (rotationDir === "cw") {
    ctx.beginPath();
    ctx.arc(arrowCx, arrowCy, arrowR, start, end, false);
    ctx.stroke();
    const headX = arrowCx + Math.cos(end) * arrowR;
    const headY = arrowCy + Math.sin(end) * arrowR;
    drawArrow(ctx, { x: headX, y: headY }, end - Math.PI / 2 + Math.PI, "#000", viewportScale);
  } else {
    ctx.beginPath();
    ctx.arc(arrowCx, arrowCy, arrowR, end, start, true);
    ctx.stroke();
    const headX = arrowCx + Math.cos(start) * arrowR;
    const headY = arrowCy + Math.sin(start) * arrowR;
    drawArrow(ctx, { x: headX, y: headY }, start + Math.PI / 2 + Math.PI, "#000", viewportScale);
  }

  ctx.restore();
};

const drawOrbitStrokesOrDecorations = (
  phase: "stroke" | "decor",
  ctx: CanvasRenderingContext2D,
  pathPoints: OrbitPoint[],
  strokeColor: string,
  rScale: OrbitRadialScale,
  showOrbitPoints: boolean,
  viewportScale: number,
) => {
  const displayPath = selectOrbitPathPoints(pathPoints);
  const pointsArrCoords = displayPath.map(point => ({
    ...point,
    ...getPolarPointCoords(point, rScale),
  }));

  if (phase === "stroke") {
    if (pointsArrCoords.length < 1) return;
    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = scaleCanvasMetric(1, viewportScale);
    pointsArrCoords.forEach((point, index) => {
      if (index === 0) {
        ctx.moveTo(point.canvasX, point.canvasY);
      } else {
        ctx.lineTo(point.canvasX, point.canvasY);
      }
    });
    ctx.stroke();
    ctx.restore();
    return;
  }

  if (showOrbitPoints) {
    drawOrbitPointMarkers(
      ctx,
      selectOrbitMarkerPoints(pathPoints).map(point => ({
        ...point,
        ...getPolarPointCoords(point, rScale),
      })),
      strokeColor,
      viewportScale,
    );
  }

  if (pointsArrCoords.length >= 2) {
    const head0 = pointsArrCoords[0];
    const nextIdx = findDifferentNeighborIndex(pointsArrCoords, 0, 1);
    if (nextIdx !== null) {
      const nextPoint = pointsArrCoords[nextIdx];
      const angle01 = Math.atan2(nextPoint.canvasY - head0.canvasY, nextPoint.canvasX - head0.canvasX);
      drawArrow(ctx, { x: head0.canvasX, y: head0.canvasY }, angle01, ARROW_COLOR, viewportScale);
    }

    const lastIdx = pointsArrCoords.length - 1;
    const headN = pointsArrCoords[lastIdx];
    const prevIdx = findDifferentNeighborIndex(pointsArrCoords, lastIdx, -1);
    if (prevIdx !== null) {
      const prevPoint = pointsArrCoords[prevIdx];
      const anglePrevToLast = Math.atan2(headN.canvasY - prevPoint.canvasY, headN.canvasX - prevPoint.canvasX);
      drawArrow(ctx, { x: headN.canvasX, y: headN.canvasY }, anglePrevToLast, ARROW_COLOR, viewportScale);
    }
  }
};

export const DrawOrbitConnectingLinesPlugin: Plugin<any> = {
  id: "drawOrbitConnectingLines",

  afterEvent(chart, args, options?: IOrbitConnectingLinesPluginOptions | false) {
    if (updateOrbitPointTooltip(chart, args.event, options)) {
      args.changed = true;
    }
  },

  beforeDatasetsDraw(chart, _args, options?: IOrbitConnectingLinesPluginOptions | false) {
    const chartType = (chart.config as any).type;

    if (chartType !== "radar" && chartType !== "line") {
      return;
    }

    const ctx = chart.ctx;
    const viewportScale = getViewportScale(options);
    const pointsArr = (chart.data as any)._points as OrbitPoint[] | undefined;
    const isCartesianOrbit = !!(chart.data as any)._isCartesianOrbit;
    if (Array.isArray(pointsArr) && pointsArr.length > 0 && isCartesianOrbit && chartType === "line") {
      return;
    }

    if (chartType === "radar") {
      const rScale = getOrbitRadialScale(chart);
      if (!rScale) {
        return;
      }

      if ((chart.data as any)._polarTrendMode) {
        const drawPolarTrendPath = (pathPoints: OrbitPoint[], strokeColor: string) => {
          const pts = pathPoints.map(p => ({
            ...p,
            ...getPolarPointCoords(p, rScale),
          }));
          if (pts.length < 2) {
            return;
          }

          ctx.save();
          ctx.beginPath();
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = scaleCanvasMetric(2, viewportScale);
          ctx.lineJoin = "round";
          ctx.lineCap = "round";
          pts.forEach((p, index) => {
            if (index === 0) {
              ctx.moveTo(p.canvasX, p.canvasY);
            } else {
              ctx.lineTo(p.canvasX, p.canvasY);
            }
          });
          ctx.stroke();
          ctx.restore();

          drawOrbitPointMarkers(ctx, pts, strokeColor, viewportScale);
        };

        chart.data.datasets.forEach((dataset, datasetIndex) => {
          if (!chart.isDatasetVisible(datasetIndex)) {
            return;
          }
          const pathPoints = (dataset as any)._orbitPathPoints as OrbitPoint[] | undefined;
          if (!Array.isArray(pathPoints) || pathPoints.length === 0) {
            return;
          }
          const strokeColor = (dataset as any).orbitStrokeColor ?? (dataset as any).pointBorderColor ?? ARROW_COLOR;
          drawPolarTrendPath(pathPoints, strokeColor);
        });
        return;
      }

      const showOrbitPoints = !!(chart.data as any)._showOrbitPoints;
      const drawStrokes = (pathPoints: OrbitPoint[], strokeColor: string) => {
        drawOrbitStrokesOrDecorations("stroke", ctx, pathPoints, strokeColor, rScale, showOrbitPoints, viewportScale);
      };

      let drewPolarPath = false;
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        if (!chart.isDatasetVisible(datasetIndex)) {
          return;
        }
        const pathPoints = (dataset as any)._orbitPathPoints as OrbitPoint[] | undefined;
        if (!Array.isArray(pathPoints) || pathPoints.length === 0) {
          return;
        }
        const strokeColor = (dataset as any).orbitStrokeColor ?? (dataset as any).pointBorderColor ?? ARROW_COLOR;
        drawStrokes(pathPoints, strokeColor);
        drewPolarPath = true;
      });

      if (!drewPolarPath && Array.isArray(pointsArr) && pointsArr.length > 0) {
        drawStrokes(pointsArr, (chart.data as any)._orbitColor ?? "rgba(255, 0, 0, 0.8)");
        drewPolarPath = true;
      }

      if (drewPolarPath) {
        return;
      }
    }

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!chart.isDatasetVisible(datasetIndex)) return;

      const itemsWithDate = (dataset as any)._itemsWithDate as { r: number; theta: number; date: Date }[];
      if (itemsWithDate && itemsWithDate.length > 0 && itemsWithDate[0].date) {
        const meta = chart.getDatasetMeta(datasetIndex);
        const sorted = itemsWithDate.slice().sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        ctx.save();
        ctx.strokeStyle = (dataset as any).pointBorderColor ?? "red";
        ctx.lineWidth = scaleCanvasMetric(2, viewportScale);
        ctx.beginPath();
        sorted.forEach((item, index) => {
          const theta = Math.round(item.theta) % 360;
          const point = meta.data[359 - theta];
          if (point) {
            if (index === 0) ctx.moveTo(point.x, point.y);
            else ctx.lineTo(point.x, point.y);
          }
        });
        ctx.stroke();
        ctx.restore();
      }
    });
  },

  afterDatasetsDraw(chart, _args, options?: IOrbitConnectingLinesPluginOptions | false) {
    const chartType = (chart.config as any).type;

    if (chartType !== "radar" && chartType !== "line") {
      return;
    }

    const ctx = chart.ctx;
    const viewportScale = getViewportScale(options);
    const pointsArr = (chart.data as any)._points as OrbitPoint[] | undefined;
    const showOrbitPoints = !!(chart.data as any)._showOrbitPoints;
    const rotationDirection = (chart.data as any)._rotationDirection;
    const isCartesianOrbit = !!(chart.data as any)._isCartesianOrbit;

    if (Array.isArray(pointsArr) && pointsArr.length > 0 && isCartesianOrbit && chartType === "line") {
      const meta = chart.getDatasetMeta(0);
      const xScaleId = typeof meta.xAxisID === "string" ? meta.xAxisID : "x";
      const yScaleId = typeof meta.yAxisID === "string" ? meta.yAxisID : "y";
      const xScale = chart.scales[xScaleId] ?? chart.scales.x;
      const yScale = chart.scales[yScaleId] ?? chart.scales.y;

      if (!xScale || !yScale) {
        return;
      }

      const displayPath = selectOrbitPathPoints(pointsArr);
      const pointsArrCoords = displayPath.map(point => ({
        ...point,
        canvasX: xScale.getPixelForValue(point.x),
        canvasY: yScale.getPixelForValue(point.y),
      }));

      if (showOrbitPoints) {
        drawOrbitPointMarkers(
          ctx,
          selectOrbitMarkerPoints(pointsArr).map(point => ({
            ...point,
            canvasX: xScale.getPixelForValue(point.x),
            canvasY: yScale.getPixelForValue(point.y),
          })),
          (chart.data as any)._orbitColor ?? "rgba(255, 0, 0, 0.8)",
          viewportScale,
        );
      }

      if (pointsArrCoords.length >= 2) {
        const head0 = pointsArrCoords[0];
        const nextIdx = findDifferentNeighborIndex(pointsArrCoords, 0, 1);

        if (nextIdx !== null) {
          const nextPoint = pointsArrCoords[nextIdx];
          const angle01 = Math.atan2(nextPoint.canvasY - head0.canvasY, nextPoint.canvasX - head0.canvasX);
          drawArrow(ctx, { x: head0.canvasX, y: head0.canvasY }, angle01, ARROW_COLOR, viewportScale);
        }

        const lastIdx = pointsArrCoords.length - 1;
        const headN = pointsArrCoords[lastIdx];
        const prevIdx = findDifferentNeighborIndex(pointsArrCoords, lastIdx, -1);

        if (prevIdx !== null) {
          const prevPoint = pointsArrCoords[prevIdx];
          const anglePrevToLast = Math.atan2(headN.canvasY - prevPoint.canvasY, headN.canvasX - prevPoint.canvasX);
          drawArrow(ctx, { x: headN.canvasX, y: headN.canvasY }, anglePrevToLast, ARROW_COLOR, viewportScale);
        }
      }

      drawRotationIndicatorCartesian(chart, rotationDirection, ctx, viewportScale);
      return;
    }

    if (chartType === "radar" && (chart.data as any)._polarTrendMode) {
      return;
    }

    if (chartType === "radar") {
      const rScale = getOrbitRadialScale(chart);
      if (!rScale) {
        return;
      }

      const drawDecors = (pathPoints: OrbitPoint[], strokeColor: string) => {
        drawOrbitStrokesOrDecorations("decor", ctx, pathPoints, strokeColor, rScale, showOrbitPoints, viewportScale);
      };

      let drewPolarPath = false;
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        if (!chart.isDatasetVisible(datasetIndex)) {
          return;
        }
        const pathPoints = (dataset as any)._orbitPathPoints as OrbitPoint[] | undefined;
        if (!Array.isArray(pathPoints) || pathPoints.length === 0) {
          return;
        }
        const strokeColor = (dataset as any).orbitStrokeColor ?? (dataset as any).pointBorderColor ?? ARROW_COLOR;
        drawDecors(pathPoints, strokeColor);
        drewPolarPath = true;
      });

      if (!drewPolarPath && Array.isArray(pointsArr) && pointsArr.length > 0) {
        drawDecors(pointsArr, (chart.data as any)._orbitColor ?? "rgba(255, 0, 0, 0.8)");
        drewPolarPath = true;
      }

      if (drewPolarPath) {
        drawRotationIndicatorPolar(chart, rotationDirection, ctx, viewportScale);
      }
    }
  },

  afterDraw(chart, _args, options?: IOrbitConnectingLinesPluginOptions | false) {
    if ((chart.config as any).type !== "radar") {
      return;
    }

    drawViewportAngularScaleLabels(chart, options);
  },
};
